import { useEffect } from 'react'

import { fetchPropertyRates } from 'actions/PropertyActions'
import { EmptyArray } from 'lib/array/arrayUtils'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'

import usePropertyOfferMapping from './Offers/usePropertyOfferMapping'
import { useAppDispatch, useAppSelector } from './reduxHooks'

interface PropertyRatesHookParams {
  lpcPropertyId: string | undefined;
  checkIn: string | undefined;
  checkOut: string | undefined;
  occupancies: Array<App.Occupants>;
  dynamicAvailability: boolean;
  utmContent: string | undefined;
  roomTypeId: string | undefined;
  roomRateId: string | undefined;
  disabled?: boolean;
}

function usePropertyRates({
  lpcPropertyId,
  checkIn,
  checkOut,
  occupancies,
  dynamicAvailability,
  utmContent,
  roomTypeId,
  roomRateId,
  disabled = false,
}: PropertyRatesHookParams): [Array<App.PropertyRoomRates>, boolean, string | undefined] {
  const dispatch = useAppDispatch()

  const [mapping] = usePropertyOfferMapping(lpcPropertyId)
  const propertyId = mapping?.propertyId
  const key = buildPropertyRatesKey({
    propertyId,
    checkIn,
    checkOut,
    occupancies,
    dynamicAvailability,
    utmContent,
    roomTypeId,
    roomRateId,
  })
  const record = useAppSelector(state => state.offer.propertyRates[key])
  const isHookEnabled = !disabled

  useEffect(() => {
    if (isHookEnabled && key) {
      dispatch(fetchPropertyRates(propertyId!, {
        checkIn: checkIn!,
        checkOut: checkOut!,
        occupancies,
        dynamicAvailability,
        utmContent,
        roomTypeId,
        roomRateId,
      }))
    }
    // NB: `key` effectively (validates and) incorporates `propertyId`, `checkIn`, `checkOut`,
    // `occupancies`, `dynamicAvailability`, `utmContent`, `roomTypeId`, and `roomRateId`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHookEnabled, key, dispatch])

  return [
    record?.rooms ?? EmptyArray,
    !!record?.fetching,
    record?.error,
  ]
}

export default usePropertyRates
